import { useState } from 'react';
import type { TemperatureScenarioRow } from '../types';
import { formatNumber, formatSignedDays } from '../utils/format';

interface Props {
  rows: TemperatureScenarioRow[];
  step: number;
  onStepChange: (step: number) => void;
}

export function TemperatureExplorer({ rows, step, onStepChange }: Props) {
  const [expanded, setExpanded] = useState(false);

  if (rows.length === 0) {
    return <p className="empty-state">Temperature scenarios appear once a valid scenario is entered.</p>;
  }

  const maxAAT = Math.max(...rows.map((r) => (Number.isFinite(r.aatDays) ? r.aatDays : 0)));

  return (
    <div className="explorer">
      <p className="explorer-lede">
        Explore how changing the accelerated temperature changes the factor and the calculated duration. The sweep is
        centred on the temperature you entered.
      </p>

      {!expanded ? (
        <button type="button" className="secondary-button" onClick={() => setExpanded(true)}>
          Explore temperature scenarios
        </button>
      ) : (
        <>
          <div className="explorer-controls">
            <label htmlFor="temp-step">Step between scenarios</label>
            <div className="field-row">
              <input
                id="temp-step"
                type="range"
                min={1}
                max={15}
                step={1}
                value={step}
                onChange={(e) => onStepChange(Number(e.target.value))}
              />
              <span className="unit-suffix">{step} °C</span>
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">T_AA</th>
                  <th scope="col">AAF</th>
                  <th scope="col">Calculated AAT</th>
                  <th scope="col">vs. your T_AA</th>
                  <th scope="col" className="col-viz">
                    Relative duration
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.acceleratedTempC} className={row.isBaseline ? 'is-baseline' : ''}>
                    <th scope="row" className="num">
                      {row.acceleratedTempC} °C
                      {row.isBaseline && <span className="baseline-flag">yours</span>}
                    </th>
                    <td className="num">{formatNumber(row.aaf, 4)}</td>
                    <td className="num">{formatNumber(row.aatDays, 3)} d</td>
                    <td className="num delta">{formatSignedDays(row.deltaDaysVsBaseline)}</td>
                    <td className="col-viz">
                      <span
                        className="bar bar-temp"
                        style={{ width: `${maxAAT > 0 ? Math.max(2, (row.aatDays / maxAAT) * 100) : 0}%` }}
                        aria-hidden="true"
                      />
                      <span className="sr-only">{formatNumber(row.aatDays, 1)} days</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.some((r) => r.note) && (
            <ul className="scenario-notes">
              {rows
                .filter((r) => r.note)
                .map((r) => (
                  <li key={`note-${r.acceleratedTempC}`}>
                    <span className="num">{r.acceleratedTempC} °C</span> — {r.note}
                  </li>
                ))}
            </ul>
          )}

          <p className="explorer-note">
            These rows are arithmetic across a temperature range. None of them is a recommended aging condition, and the
            table is deliberately unordered by preference. Whether a temperature is suitable depends on materials,
            transitions, and mechanisms this tool knows nothing about.
          </p>
        </>
      )}
    </div>
  );
}
